import React, { useMemo, useState } from "react";
import { MessageCircle, Phone, MapPin, Globe, Clock, CheckCircle, Send } from "lucide-react";

const toAlignClass = (a) => {
  const v = String(a || "").toLowerCase().trim();
  if (v === "left") return "text-left";
  if (v === "right") return "text-right";
  if (v === "center") return "text-center";
  return "";
};

const buildBg = (style, defaultClass) => {
  const mode = style?.bgMode || "default";

  if (mode === "solid" && style?.bgColor) {
    return { style: { backgroundColor: style.bgColor }, className: "" };
  }

  if (mode === "gradient") {
    const from = (style?.bgGradientFrom || "").trim();
    const via = (style?.bgGradientVia || "").trim();
    const to = (style?.bgGradientTo || "").trim();
    const angleNum = Number(style?.bgGradientAngle);
    const angle = Number.isFinite(angleNum) ? angleNum : 180;

    if (from && to) {
      const stops = via ? `${from}, ${via}, ${to}` : `${from}, ${to}`;
      return { style: { backgroundImage: `linear-gradient(${angle}deg, ${stops})` }, className: "" };
    }
  }

  return { style: undefined, className: defaultClass };
};

const toDigits = (s) => String(s || "").replace(/\D/g, "");

const DEFAULTS = {
  title: "Get in Touch",
  subtitle: "Choose your preferred way to connect with our team.",

  tabWhatsAppLabel: "WhatsApp Us",
  tabPhoneLabel: "Call Us",

  formNamePlaceholder: "Your Name",
  formPhonePlaceholder: "Your Phone Number",
  formMessagePlaceholder: "Your Message",

  buttonText: "Send via WhatsApp",

  successTitle: "Opening WhatsApp...",
  successSubtitle: "Your message is ready to send",

  whatsappCardTitle: "WhatsApp",
  whatsappCardValue: "Chat with us",
  whatsappCardNote: "Fast replies on WhatsApp",

  phoneCardTitle: "Phone",
  phoneCardValue: "Call us directly",
  phoneCardNote: "Business contact number",

  officeCardTitle: "Office",
  officeCardValue: "Business address here",

  phonePanelTitle: "Call our team",
  phonePanelSubtitle: "Use the business phone number below",
  phoneWaitText: "We will be happy to help you",
  phoneCallNowText: "Call Now",
  phoneIntlText: "This will call the business phone from the page settings.",

  metric1Label: "WhatsApp Support",
  metric1Value: "Fast",
  metric2Label: "Phone Access",
  metric2Value: "Direct",
};

function buildWhatsAppText(cfg, formData) {
  return [
    `*${cfg.title || "New Contact Request"}*`,
    "",
    `Name: ${formData.name || "-"}`,
    `Phone: ${formData.phone || "-"}`,
    `Message: ${formData.message || "-"}`,
  ].join("\n");
}

const COLOR_CLASSES = {
  blue: { activeText: "text-blue-600", activeBg: "bg-blue-50", underline: "bg-blue-600", icon: "text-blue-600" },
  green: { activeText: "text-green-600", activeBg: "bg-green-50", underline: "bg-green-600", icon: "text-green-600" },
};

export default function CTASection3({ config, style, businessPhone }) {
  const cfg = { ...DEFAULTS, ...(config || {}) };

  const [activeTab, setActiveTab] = useState("whatsapp");
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({ name: "", phone: "", message: "" });

  const bg = buildBg(style, "bg-gray-50");
  const direction = (cfg.direction || "ltr").toLowerCase();

  const align = (style?.textAlign || "").trim();
  const hasAlign = align.length > 0;
  const headerAlignClass = hasAlign ? toAlignClass(align) : "text-left";
  const contentAlignClass = hasAlign ? toAlignClass(align) : "";

  const rawPrimary = cfg.buttonText;
  const showPrimary = rawPrimary === undefined ? true : String(rawPrimary).trim().length > 0;
  const primaryLabel = rawPrimary === undefined ? "Send via WhatsApp" : rawPrimary;

  const primaryHasCustom = Boolean(
    String(style?.primaryBtnBg || "").trim() || String(style?.primaryBtnText || "").trim()
  );

  const targetPhone = toDigits(
    businessPhone ||
      config?.businessPhone ||
      cfg.businessPhone ||
      cfg.whatsappNumber ||
      cfg.phoneCardValue ||
      ""
  );

  const contactMethods = useMemo(
    () => [
      { id: "whatsapp", Icon: MessageCircle, label: cfg.tabWhatsAppLabel, color: "blue" },
      { id: "phone", Icon: Phone, label: cfg.tabPhoneLabel, color: "green" },
    ],
    [cfg.tabWhatsAppLabel, cfg.tabPhoneLabel]
  );

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!targetPhone) {
      alert("Business WhatsApp phone is missing. Please add it in General tab.");
      return;
    }

    if (!formData.name || !formData.phone || !formData.message) return;

    const text = encodeURIComponent(buildWhatsAppText(cfg, formData));
    setSubmitted(true);
    window.open(`https://wa.me/${targetPhone}?text=${text}`, "_blank");

    setTimeout(() => {
      setSubmitted(false);
      setFormData({ name: "", phone: "", message: "" });
    }, 1800);
  };

  const infoCardStyle = {
    backgroundColor: style?.ctaInfoCardBg || undefined,
    borderColor: style?.ctaInfoCardBorder || undefined,
    borderWidth: style?.ctaInfoCardBorder ? "1px" : undefined,
  };

  const mainCardStyle = {
    backgroundColor: style?.ctaCardBg || undefined,
    borderColor: style?.ctaCardBorder || undefined,
    borderWidth: style?.ctaCardBorder ? "1px" : undefined,
  };

  const metricCardStyle = {
    backgroundColor: style?.ctaMetricCardBg || undefined,
    borderColor: style?.ctaMetricCardBorder || undefined,
    borderWidth: style?.ctaMetricCardBorder ? "1px" : undefined,
  };

  const inputStyle = {
    backgroundColor: style?.ctaInputBg || undefined,
    borderColor: style?.ctaInputBorder || undefined,
    color: style?.ctaInputText || undefined,
  };

  const getTabStyle = (active) => ({
    backgroundColor: active
      ? style?.ctaTabActiveBg || undefined
      : style?.ctaTabInactiveBg || undefined,
    color: active
      ? style?.ctaTabActiveText || undefined
      : style?.ctaTabInactiveText || undefined,
    borderColor: style?.ctaTabBorder || undefined,
  });

  const renderContactForm = () => {
    if (activeTab === "whatsapp") {
      return (
        <form onSubmit={handleSubmit} className={`space-y-4 ${contentAlignClass}`}>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
            placeholder={cfg.formNamePlaceholder}
            style={inputStyle}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none transition"
            required
          />
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData((p) => ({ ...p, phone: e.target.value }))}
            placeholder={cfg.formPhonePlaceholder}
            style={inputStyle}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none transition"
            required
          />
          <textarea
            rows="4"
            value={formData.message}
            onChange={(e) => setFormData((p) => ({ ...p, message: e.target.value }))}
            placeholder={cfg.formMessagePlaceholder}
            style={inputStyle}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none transition resize-none"
            required
          />

          {showPrimary && (
            <button
              type="submit"
              style={
                primaryHasCustom
                  ? { backgroundColor: style?.primaryBtnBg || undefined, color: style?.primaryBtnText || undefined }
                  : undefined
              }
              className={`w-full font-bold py-3 px-6 rounded-lg transition-all duration-300 hover:shadow-lg flex items-center justify-center ${
                primaryHasCustom ? "hover:opacity-90" : "bg-green-600 hover:bg-green-700 text-white"
              }`}
            >
              {primaryLabel}
              <Send className="ms-2 w-4 h-4 mr-2" />
            </button>
          )}
        </form>
      );
    }

    if (activeTab === "phone") {
      return (
        <div className={`space-y-6 ${contentAlignClass}`}>
          <div
            style={{
              backgroundColor: style?.ctaPanelBg || undefined,
              borderColor: style?.ctaPanelBorder || undefined,
              borderWidth: style?.ctaPanelBorder ? "1px" : undefined,
            }}
            className="rounded-xl p-6 text-center bg-green-50"
          >
            <Phone
              style={{ color: style?.ctaAccentText || undefined }}
              className="w-16 h-16 mx-auto mb-4 text-green-600"
            />
            <h3
              style={{ color: style?.ctaInfoTitleColor || undefined }}
              className="text-3xl font-bold text-gray-900 mb-2"
            >
              {cfg.phonePanelTitle}
            </h3>
            <p
              style={{ color: style?.ctaInfoTextColor || style?.ctaMutedTextColor || undefined }}
              className="text-gray-600 mb-4"
            >
              {cfg.phonePanelSubtitle}
            </p>
            <div
              style={{ color: style?.ctaMutedTextColor || undefined }}
              className="flex items-center justify-center space-x-2 text-sm text-gray-500"
            >
              <Clock className="w-4 h-4" />
              <span>{cfg.phoneWaitText}</span>
            </div>
          </div>

          <button
            onClick={() => {
              if (!targetPhone) {
                alert("Business phone is missing. Please add it in General tab.");
                return;
              }
              window.location.href = `tel:+${targetPhone}`;
            }}
            style={
              primaryHasCustom
                ? {
                    backgroundColor: style?.primaryBtnBg || undefined,
                    color: style?.primaryBtnText || undefined,
                  }
                : undefined
            }
            className={`w-full font-bold py-4 px-4 rounded-xl transition-colors flex items-center justify-center ${
              primaryHasCustom ? "hover:opacity-90" : "bg-green-600 hover:bg-green-700 text-white"
            }`}
          >
            <Phone className="w-5 h-5 mr-2" />
            {cfg.phoneCallNowText}
          </button>

          <div
            style={{
              backgroundColor: style?.ctaAccentBg || undefined,
              color: style?.ctaAccentText || undefined,
              borderColor: style?.ctaAccentBorder || undefined,
              borderWidth: style?.ctaAccentBorder ? "1px" : undefined,
            }}
            className="rounded-lg p-4 bg-blue-50"
          >
            <p className="text-sm text-center">{cfg.phoneIntlText}</p>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <section dir={direction} style={bg.style} className={`py-20 px-4 ${bg.className}`}>
      <div className="container mx-auto max-w-6xl">
        <div className="grid lg:grid-cols-2 gap-12">
          <div className={contentAlignClass}>
            <h2
              style={{ color: style?.titleColor || undefined }}
              className={`text-3xl md:text-4xl font-bold text-gray-900 mb-4 ${headerAlignClass}`}
            >
              {cfg.title}
            </h2>

            <p
              style={{ color: style?.subtitleColor || undefined }}
              className={`text-lg text-gray-600 mb-8 ${headerAlignClass}`}
            >
              {cfg.subtitle}
            </p>

            <div className="space-y-6 mb-8">
              <div style={infoCardStyle} className="flex items-center p-4 rounded-xl shadow-sm hover:shadow-md transition-shadow bg-white">
                <div
                  style={{
                    backgroundColor: style?.ctaAccentBg || undefined,
                    color: style?.ctaAccentText || undefined,
                    borderColor: style?.ctaAccentBorder || undefined,
                    borderWidth: style?.ctaAccentBorder ? "1px" : undefined,
                  }}
                  className="w-12 h-12 rounded-full flex items-center justify-center me-4 bg-blue-100"
                >
                  <MessageCircle className="w-6 h-6" />
                </div>
                <div>
                  <h3
                    style={{ color: style?.ctaInfoTitleColor || undefined }}
                    className="font-semibold text-gray-900"
                  >
                    {cfg.whatsappCardTitle}
                  </h3>
                  <p
                    style={{ color: style?.ctaInfoTextColor || undefined }}
                    className="text-gray-600"
                  >
                    {cfg.whatsappCardValue || `+${targetPhone}`}
                  </p>
                  <p
                    style={{ color: style?.ctaInfoNoteColor || style?.ctaMutedTextColor || undefined }}
                    className="text-xs text-gray-500 mt-1"
                  >
                    {cfg.whatsappCardNote}
                  </p>
                </div>
              </div>

              <div style={infoCardStyle} className="flex items-center p-4 rounded-xl shadow-sm hover:shadow-md transition-shadow bg-white">
                <div
                  style={{
                    backgroundColor: style?.ctaAccentBg || undefined,
                    color: style?.ctaAccentText || undefined,
                    borderColor: style?.ctaAccentBorder || undefined,
                    borderWidth: style?.ctaAccentBorder ? "1px" : undefined,
                  }}
                  className="w-12 h-12 rounded-full flex items-center justify-center me-4 bg-green-100"
                >
                  <Phone className="w-6 h-6" />
                </div>
                <div>
                  <h3
                    style={{ color: style?.ctaInfoTitleColor || undefined }}
                    className="font-semibold text-gray-900"
                  >
                    {cfg.phoneCardTitle}
                  </h3>
                  <p
                    style={{ color: style?.ctaInfoTextColor || undefined }}
                    className="text-gray-600"
                  >
                    {cfg.phoneCardValue || `+${targetPhone}`}
                  </p>
                  <p
                    style={{ color: style?.ctaInfoNoteColor || style?.ctaMutedTextColor || undefined }}
                    className="text-xs text-gray-500 mt-1"
                  >
                    {cfg.phoneCardNote}
                  </p>
                </div>
              </div>

              <div style={infoCardStyle} className="flex items-center p-4 rounded-xl shadow-sm hover:shadow-md transition-shadow bg-white">
                <div
                  style={{
                    backgroundColor: style?.ctaAccentBg || undefined,
                    color: style?.ctaAccentText || undefined,
                    borderColor: style?.ctaAccentBorder || undefined,
                    borderWidth: style?.ctaAccentBorder ? "1px" : undefined,
                  }}
                  className="w-12 h-12 rounded-full flex items-center justify-center me-4 bg-purple-100"
                >
                  <MapPin className="w-6 h-6" />
                </div>
                <div>
                  <h3
                    style={{ color: style?.ctaInfoTitleColor || undefined }}
                    className="font-semibold text-gray-900"
                  >
                    {cfg.officeCardTitle}
                  </h3>
                  <p
                    style={{ color: style?.ctaInfoTextColor || undefined }}
                    className="text-gray-600 whitespace-pre-line"
                  >
                    {cfg.officeCardValue}
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div
                style={metricCardStyle}
                className={`rounded-xl p-4 bg-white ${hasAlign ? toAlignClass(align) : "text-center"}`}
              >
                <Globe
                  style={{ color: style?.ctaAccentText || undefined }}
                  className="w-6 h-6 mx-auto mb-2 text-blue-600"
                />
                <div
                  style={{ color: style?.ctaMetricValueColor || undefined }}
                  className="text-2xl font-bold text-gray-900"
                >
                  {cfg.metric1Value}
                </div>
                <div
                  style={{ color: style?.ctaMetricLabelColor || style?.ctaMutedTextColor || undefined }}
                  className="text-sm text-gray-600"
                >
                  {cfg.metric1Label}
                </div>
              </div>
              <div
                style={metricCardStyle}
                className={`rounded-xl p-4 bg-white ${hasAlign ? toAlignClass(align) : "text-center"}`}
              >
                <Clock
                  style={{ color: style?.ctaAccentText || undefined }}
                  className="w-6 h-6 mx-auto mb-2 text-green-600"
                />
                <div
                  style={{ color: style?.ctaMetricValueColor || undefined }}
                  className="text-2xl font-bold text-gray-900"
                >
                  {cfg.metric2Value}
                </div>
                <div
                  style={{ color: style?.ctaMetricLabelColor || style?.ctaMutedTextColor || undefined }}
                  className="text-sm text-gray-600"
                >
                  {cfg.metric2Label}
                </div>
              </div>
            </div>
          </div>

          <div style={mainCardStyle} className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="flex border-b">
              {contactMethods.map(({ id, Icon, label, color }) => {
                const c = COLOR_CLASSES[color] || COLOR_CLASSES.blue;
                const active = activeTab === id;

                return (
                  <button
                    key={id}
                    onClick={() => setActiveTab(id)}
                    style={getTabStyle(active)}
                    className={`flex-1 py-5 px-4 text-base font-semibold transition-all relative ${
                      active ? `${c.activeText} ${c.activeBg}` : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    }`}
                  >
                    <Icon className={`w-5 h-5 inline-block me-2 ${active ? c.icon : "text-gray-400"}`} />
                    {label}
                    {active && (
                      <div
                        style={{ backgroundColor: style?.ctaTabBorder || undefined }}
                        className={`absolute bottom-0 left-0 right-0 h-0.5 ${!style?.ctaTabBorder ? c.underline : ""}`}
                      />
                    )}
                  </button>
                );
              })}
            </div>

            <div className="p-6">
              {submitted ? (
                <div className={`${contentAlignClass} py-12`}>
                  <div
                    style={{ backgroundColor: style?.ctaSuccessIconBg || undefined }}
                    className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
                  >
                    <CheckCircle
                      style={{ color: style?.ctaSuccessIconColor || undefined }}
                      className="w-10 h-10 text-green-600"
                    />
                  </div>
                  <h3
                    style={{ color: style?.ctaSuccessTitleColor || undefined }}
                    className="text-2xl font-bold text-gray-900 mb-2"
                  >
                    {cfg.successTitle}
                  </h3>
                  <p
                    style={{ color: style?.ctaSuccessSubtitleColor || style?.ctaMutedTextColor || undefined }}
                    className="text-gray-600"
                  >
                    {cfg.successSubtitle}
                  </p>
                </div>
              ) : (
                renderContactForm()
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}